import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    goal: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    await axios.post("/api/contact", {
      name: formData.name,
      email: formData.email,
      company: formData.company,
      phone: formData.phone,
      goal: formData.goal,
      message: formData.message,
    });

    toast.success("Thank you! We'll get back to you within 24 hours.");

    setFormData({
      name: "",
      email: "",
      company: "",
      phone: "",
      goal: "",
      message: "",
    });
  } catch (error) {
    console.error("Error submitting form:", error);
    toast.error("Failed to submit. Please try again.");
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="min-h-screen noise-texture pt-32">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-brandSecondary font-medium text-sm uppercase tracking-wider" data-testid="contact-label">
              Get in Touch
            </span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mt-4 mb-6" data-testid="contact-heading">
              Let's Build Your
              <br />
              <span className="text-gradient">Growth Strategy</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed" data-testid="contact-intro">
              Book a free 30-minute strategy call or send us a message. 
              We typically respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="contact-form-section"
            >
              <div className="glass-card rounded-3xl p-8">
                <h2 className="font-heading font-bold text-3xl text-white mb-6" data-testid="form-heading">
                  Send Us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="form-input-name"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="form-input-email"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="form-input-company"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
                        placeholder="Your company"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="form-input-phone"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
                        placeholder="+91 12345 67890"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="goal" className="block text-sm font-medium text-gray-300 mb-2">
                      Primary Goal
                    </label>
                    <select
                      id="goal"
                      name="goal"
                      value={formData.goal}
                      onChange={handleChange}
                      data-testid="form-input-goal"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
                    >
                      <option value="">Select your primary goal</option>
                      <option value="strategy">Develop Marketing Strategy</option>
                      <option value="brand">Build Brand Communication</option>
                      <option value="automation">Implement AI Automation</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      data-testid="form-input-message"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:border-transparent resize-none"
                      placeholder="Tell us about your business and what you're looking to achieve..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit-button"
                    className="w-full bg-brandPrimary hover:bg-brandPrimary/90 text-white px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-brandPrimary/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send size={20} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Calendar Booking & Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              {/* Calendly Placeholder */}
              <div className="glass-card rounded-3xl p-8" data-testid="calendly-section">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar size={32} className="text-brandPrimary" />
                  <h2 className="font-heading font-bold text-3xl text-white">
                    Book a Strategy Call
                  </h2>
                </div>
                
                <p className="text-gray-400 mb-6" data-testid="calendly-description">
                  Prefer to schedule directly? Book a free 30-minute strategy consultation 
                  at a time that works for you.
                </p>

                {/* Calendly Integration Placeholder */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center" data-testid="calendly-placeholder">
                  <Calendar size={48} className="mx-auto text-brandPrimary mb-4" />
                  <p className="text-gray-400 mb-4">
                    Calendly integration ready
                  </p>
                  <p className="text-gray-500 text-sm">
                    Admin: Add your Calendly link to enable direct booking
                  </p>
                  <div className="mt-6 p-4 bg-brandPrimary/10 border border-brandPrimary/20 rounded-xl">
                    <p className="text-brandPrimary text-sm font-mono">
                      Integration Point: Add Calendly embed code here
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="glass-card rounded-3xl p-8" data-testid="contact-info-section">
                <h3 className="font-heading font-semibold text-2xl text-white mb-6">
                  Other Ways to Connect
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4" data-testid="contact-email">
                    <div className="text-brandPrimary mt-1">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Email</p>
                      <a href="mailto:hello@mediapointsgroup.com" className="text-gray-400 hover:text-brandPrimary transition-colors">
                        hello@mediapointsgroup.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-phone">
                    <div className="text-brandPrimary mt-1">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Phone</p>
                      <a href="tel:+919876543210" className="text-gray-400 hover:text-brandPrimary transition-colors">
                        +91 98765 43210
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-location">
                    <div className="text-brandPrimary mt-1">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Location</p>
                      <p className="text-gray-400">
                        India<br />
                        Serving businesses nationwide
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="glass-card rounded-2xl p-6 border-l-4 border-brandSecondary" data-testid="response-info">
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">Quick Response:</strong> We typically respond to all inquiries 
                  within 24 hours on business days. For urgent matters, please call us directly.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
